import cx from "classnames";
import React, { useCallback, useMemo } from "react";
import { useProductConfiguratorStore } from "components/_ProductConfigurator/store/ProductConfiguratorStore";
import { ProductConfiguratorInput } from "./ProductConfiguratorInput";

import s from "../../ProductConfiguratorComponent.module.scss";

export const ProductConfiguratorPart = ({
  part,
  configuration,
  activePart,
  isModalView,
  isConfigurationModalOpened,
  seriesId,
  setActivePart,
  modalBodyRef,
  allowedIterablePartsSet,
}) => {
  const { changeProduct } = useProductConfiguratorStore.use.actions();

  const onChangeHandler = useCallback(
    (args) =>
      changeProduct({
        isAccessoryAffected: true,

        ...args,
      }),
    [changeProduct],
  );

  const isDisabled = part.state === 1 || part.state === 3;
  // state 2: The part/option is not relevant to the order code (i.e., its presentation does not contribute to the order code)
  // https://dev.azure.com/DEVLY-IT-Software-Development/TROX-EB-Website_v2/_wiki/wikis/TROX-System-Configurator.wiki/1132/Product-states
  const notRelevantToOrderCodeOptions = useMemo(
    () =>
      part.options?.filter((option) => !option.state || option.state < 2) || [],
    [part.options],
  );

  const isActive = useMemo(
    () => activePart === part.key,
    [activePart, part.key],
  );

  const handlePartClick = useCallback(() => {
    setActivePart(part.key);

    const modalBody = modalBodyRef?.current;
    if (modalBody) {
      const { scrollTop, clientHeight, scrollHeight } = modalBody;
      const threshold = 300;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom < threshold;

      if (isNearBottom) {
        modalBody.scrollTo({
          top: scrollHeight,
          behavior: "smooth",
        });
      }
    }
  }, [setActivePart, part.key, modalBodyRef]);

  return (
    <div
      className="mt-3"
      onClick={handlePartClick}
      id={
        isConfigurationModalOpened
          ? `modal-part-id-${part.key}`
          : `part-id-${part.key}`
      }
    >
      <a
        href={
          isConfigurationModalOpened
            ? `#modal-part-id-${part.key}`
            : `#part-id-${part.key}`
        }
        onClick={(e) => {
          e.preventDefault();
        }}
        draggable={false}
        className="text-reset text-decoration-none"
        aria-label={`Scroll to ${part.title}`}
        tabIndex="-1"
      />
      {(!isModalView || isConfigurationModalOpened) && (
        <h5
          className={cx("text-nowrap", {
            "text-info": isActive,
          })}
        >
          {part.title}
        </h5>
      )}
      <div className="d-flex flex-wrap gap-4">
        {notRelevantToOrderCodeOptions.map((option) => (
          <div
            className={cx(
              "d-flex flex-column flex-grow-1 flex-shrink-2 px-4 py-3 bg-white border rounded-2",
              {
                "bg-gray-100": isModalView,
                "border-info border": isActive,
                "border-gray-300": !isActive,
                "border-white": !isActive && !isModalView,
              },
              s.card,
            )}
            key={option.key}
          >
            <ProductConfiguratorInput
              option={option}
              onChange={onChangeHandler}
              configuration={configuration}
              partKey={part.key}
              isModalView={isModalView}
              seriesId={+seriesId}
              isDisabled={isDisabled}
              handlePartClick={handlePartClick}
              allowedIterablePartsSet={allowedIterablePartsSet}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
